import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Platform, Alert } from 'react-native';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { useGamification } from '@/hooks/useGamification';
import { useCampaignConfig } from '@/hooks/useCampaignConfig';
import { useCountry } from '@/hooks/useCountry';
import { supabase } from '@/lib/supabase';
import { withSupabaseResult } from '@/utils/supabase/withSupabaseResult';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const subLog = createScopedLogger('SUBSCRIPTION');
import { REVENUECAT_CONFIG } from '@/utils/revenueCatConfig';
import { revenueCatService } from '@/services/revenueCatService';
import { revenueCatWebService } from '@/services/revenueCatWebService';
import { campaignCache } from '@/services/campaignCacheService';
import { appleReceiptService } from '@/services/appleReceiptService';
import { isEpisodeFree } from '@/utils/isEpisodeFree';
import { revenueCatInitializer } from '@/services/revenueCatInitializer';
import { userPreferencesService } from '@/services/userPreferencesService';
import { useCoinCelebration } from '@/contexts/CoinCelebrationContext';
import { isEpisodePurchased } from '@/services/episodePurchaseStorage';
import {
  trackPurchaseInitiated,
  trackPurchaseSuccess,
  trackPurchaseCancel,
  trackPurchaseFailed,
  PaywallProductInfo,
} from '@/src/analytics';

export interface Product {
  productId: string;
  price: string;
  priceNumeric: number;
  title: string;
  description: string;
  localizedPrice?: string;
  currencyCode?: string;
  isFallbackPrice?: boolean;
}

type SubscriptionState = 'ACTIVE' | 'GRACE' | 'NOTBILLED_DENY' | null;

interface SubscriptionContextValue {
  isSubscribed: boolean;
  isLoading: boolean;
  isPurchasing: boolean;
  products: Product[];
  isIAPReady: boolean;
  webConfigError: boolean;
  subscriptionState: SubscriptionState;
  gracePeriodEndDate: string | null;
  dcbAnonymousAccess: boolean;
  dcbAccessExpiresAt: string | null;
  purchaseSubscription: (productId?: string) => Promise<void>;
  restorePurchases: () => Promise<void>;
  checkSubscriptionStatus: () => Promise<void>;
  updateSubscriptionStatus: (isSubscribed: boolean) => Promise<void>;
  canAccessEpisode: (isFree: boolean | string | number | null | undefined, episodeId?: string) => boolean;
  episodePriceCoins: number;
  deductCoinsForEpisode: (price: number, episodeId: string) => Promise<boolean>;
  forceInitializeRevenueCat: (smartuserId: string) => Promise<void>;
  forceRefreshSubscriptionFromDatabase: () => Promise<boolean>;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { authState, updateUserSubscription } = useAuth();
  const { processEvent, userGamification } = useGamification();
  const { campaignCountriesLanguagesId } = useCampaignConfig();
  const { countryCode } = useCountry();
  const { triggerCelebration } = useCoinCelebration();

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [isIAPReady, setIsIAPReady] = useState(false);
  const [webConfigError, setWebConfigError] = useState(false);
  const [episodePriceCoins, setEpisodePriceCoins] = useState(0);
  const [subscriptionState, setSubscriptionState] = useState<SubscriptionState>(null);
  const [gracePeriodEndDate, setGracePeriodEndDate] = useState<string | null>(null);
  const [dcbAnonymousAccess, setDcbAnonymousAccess] = useState(false);
  const [dcbAccessExpiresAt, setDcbAccessExpiresAt] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  const productsLoadedRef = useRef(false);
  const revenueCatInitializedRef = useRef(false);
  const lastLoggedInUserRef = useRef<string | null>(null);
  const initializationInProgressRef = useRef(false);
  const listenerCleanupRef = useRef<(() => void) | null>(null);
  const forceInitializeRevenueCatRef = useRef<((smartuserId: string) => Promise<void>) | null>(null);

  const authStateUserRef = useRef(authState.user);
  const userGamificationRef = useRef(userGamification);
  const countryCodeRef = useRef(countryCode);

  useEffect(() => {
    authStateUserRef.current = authState.user;
  }, [authState.user]);

  useEffect(() => {
    userGamificationRef.current = userGamification;
  }, [userGamification]);

  useEffect(() => {
    countryCodeRef.current = countryCode;
  }, [countryCode]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updateSubscriptionInDatabase = useCallback(async (isActive: boolean) => {
    const timestamp = new Date().toISOString();

    console.log('🛒 💾 [DB UPDATE] ===== DATABASE UPDATE TRIGGERED =====');
    console.log('🛒 💾 [DB UPDATE] Timestamp:', timestamp);
    console.log('🛒 💾 [DB UPDATE] User ID:', authState.user?.smartuserId);
    console.log('🛒 💾 [DB UPDATE] New is_paying value:', isActive);

    if (!authState.user?.smartuserId) {
      console.log('🛒 💾 [DB UPDATE] ❌ No user ID, skipping database update');
      return;
    }

    if (!isActive) {
      try {
        const { data: dbUser } = await withSupabaseResult<{ payment_method: string | null; is_paying: boolean | null; subscription_state: string | null } | null>(
          supabase
            .from('users')
            .select('payment_method, is_paying, subscription_state')
            .eq('smartuser_id', authState.user.smartuserId)
            .maybeSingle(),
          { operation: 'subscription.checkPaymentMethod', logger: subLog }
        );

        if (dbUser?.payment_method === 'dcb') {
          console.log('🛒 💾 [DB UPDATE] DCB user detected in database - refusing to set is_paying to false');
          return;
        }

        if (dbUser?.subscription_state === 'ACTIVE') {
          console.log('🛒 💾 [DB UPDATE] subscription_state=ACTIVE in database - refusing non-authoritative downgrade');
          return;
        }

        if (dbUser?.is_paying === true && Platform.OS === 'web') {
          console.log('🛒 💾 [DB UPDATE] DB is_paying=true on web - refusing RevenueCat-web downgrade (webhook is authoritative)');
          return;
        }
      } catch (checkError) {
        console.warn('🛒 💾 [DB UPDATE] Could not verify payment_method, proceeding with update');
      }
    }

    try {
      const updateStartTime = Date.now();
      const success = await userPreferencesService.updateIsPaying(authState.user.smartuserId, isActive);
      const updateEndTime = Date.now();

      if (!success) {
        console.error('🛒 💾 [DB UPDATE] ❌ Database update FAILED');
        console.error('🛒 💾 [DB UPDATE] Duration:', updateEndTime - updateStartTime, 'ms');
      } else {
        console.log('🛒 💾 [DB UPDATE] ✅ Database and cache update SUCCESSFUL');
        console.log('🛒 💾 [DB UPDATE] Duration:', updateEndTime - updateStartTime, 'ms');
        console.log('🛒 💾 [DB UPDATE] Updated is_paying to:', isActive);
        console.log('🛒 💾 [DB UPDATE] Updated at:', timestamp);
      }
    } catch (error) {
      console.error('🛒 💾 [DB UPDATE] ❌ Exception during database update:', error);
    }

    console.log('🛒 💾 [DB UPDATE] ========================================');
  }, [authState.user?.smartuserId]);

  const forceRefreshSubscriptionFromDatabase = useCallback(async (retryCount: number = 0): Promise<boolean> => {
    const smartuserId = authState.user?.smartuserId;
    const maxRetries = 3;
    const retryDelays = [2000, 5000, 10000];

    console.log('🛒 🔄 [FORCE REFRESH] ===== FORCING SUBSCRIPTION REFRESH FROM DATABASE =====');
    console.log('🛒 🔄 [FORCE REFRESH] Retry attempt:', retryCount + 1, '/', maxRetries);
    console.log('🛒 🔄 [FORCE REFRESH] User ID:', smartuserId);

    if (!smartuserId) {
      console.log('🛒 🔄 [FORCE REFRESH] ❌ No user ID available');
      return false;
    }

    try {
      const { data, error } = await withSupabaseResult<{ is_paying: boolean; subscription_state: string | null; grace_period_end_date: string | null } | null>(
        supabase
          .from('users')
          .select('is_paying, subscription_state, grace_period_end_date')
          .eq('smartuser_id', smartuserId)
          .maybeSingle(),
        { operation: 'subscription.forceRefreshFromDatabase', logger: subLog }
      );

      if (error) {
        console.error('🛒 🔄 [FORCE REFRESH] ❌ Database query error:', error.message);
        return false;
      }

      const isPayingFromDB = data?.is_paying === true;
      console.log('🛒 🔄 [FORCE REFRESH] Database is_paying value:', isPayingFromDB);

      if (isPayingFromDB) {
        console.log('🛒 🔄 [FORCE REFRESH] ✅ Subscription confirmed in database!');

        if (isMountedRef.current) {
          setIsSubscribed(true);
          setSubscriptionState(data?.subscription_state || null);
          setGracePeriodEndDate(data?.grace_period_end_date || null);
        }

        if (authState.user?.isSubscribed !== true) {
          updateUserSubscription(true);
        }

        const currentPrefs = userPreferencesService.getIsPaying(smartuserId);
        if (currentPrefs !== true) {
          await userPreferencesService.updateIsPaying(smartuserId, true);
          console.log('🛒 🔄 [FORCE REFRESH] Cache updated to is_paying: true');
        }

        console.log('🛒 🔄 [FORCE REFRESH] ========================================');
        return true;
      }

      if (retryCount < maxRetries - 1) {
        const nextDelay = retryDelays[retryCount] || 5000;
        console.log('🛒 🔄 [FORCE REFRESH] ⏳ Subscription not yet confirmed, retrying in', nextDelay, 'ms...');

        await new Promise(resolve => setTimeout(resolve, nextDelay));
        return forceRefreshSubscriptionFromDatabase(retryCount + 1);
      }

      console.log('🛒 🔄 [FORCE REFRESH] ⚠️ Subscription not confirmed after all retries');
      console.log('🛒 🔄 [FORCE REFRESH] ========================================');
      return false;
    } catch (error) {
      console.error('🛒 🔄 [FORCE REFRESH] ❌ Exception during refresh:', error);
      return false;
    }
  }, [authState.user?.smartuserId, authState.user?.isSubscribed, updateUserSubscription]);

  const updateSubscriptionStatusFromCustomerInfo = useCallback((customerInfo: any, explicitUserId?: string) => {
    const originalAppUserId = customerInfo?.originalAppUserId;
    const expectedUserId = explicitUserId || authState.user?.smartuserId?.toString();

    if (originalAppUserId?.startsWith('$RCAnonymousID:')) {
      console.log('🛒 📱 [REVENUECAT] Skipping anonymous customer info (expected during initialization)');
      console.log('🛒 📱 [REVENUECAT] Anonymous ID:', originalAppUserId, '| Expected:', expectedUserId);
      return;
    }

    if (expectedUserId && originalAppUserId && originalAppUserId !== expectedUserId) {
      console.warn('🛒 📱 [REVENUECAT] ⚠️ WARNING: Customer info user ID mismatch');
      console.warn('🛒 📱 [REVENUECAT] Customer info originalAppUserId:', originalAppUserId);
      console.warn('🛒 📱 [REVENUECAT] Expected user ID:', expectedUserId);
      console.warn('🛒 📱 [REVENUECAT] This may indicate stale data, proceeding with caution...');
    }

    const entitlementId = Platform.OS === 'ios'
      ? REVENUECAT_CONFIG.ios.entitlementId
      : Platform.OS === 'web'
        ? REVENUECAT_CONFIG.web.entitlementId
        : REVENUECAT_CONFIG.android.entitlementId;

    if (!entitlementId) {
      console.warn(`🛒 📱 [REVENUECAT] ⚠️ No entitlement ID configured for ${Platform.OS}`);
      return;
    }

    const activeEntitlementsMap = customerInfo.entitlements?.active || {};
    const isActive = Boolean(activeEntitlementsMap[entitlementId]);

    if (!isActive && authState.user?.paymentMethod === 'dcb') {
      console.log('🛒 📱 [REVENUECAT] Skipping RevenueCat downgrade for DCB user - subscription managed by operator');
      return;
    }

    if (!isActive && authState.user?.smartuserId) {
      (async () => {
        try {
          const { data: dbUser } = await withSupabaseResult<{ is_paying: boolean | null; subscription_state: string | null; payment_method: string | null } | null>(
            supabase
              .from('users')
              .select('is_paying, subscription_state, payment_method')
              .eq('smartuser_id', authState.user!.smartuserId)
              .maybeSingle(),
            { operation: 'subscription.rcDowngradeGuard', logger: subLog }
          );

          if (dbUser?.payment_method === 'dcb' || dbUser?.subscription_state === 'ACTIVE' || (dbUser?.is_paying === true && Platform.OS === 'web')) {
            console.log('🛒 📱 [REVENUECAT] DB authoritative (state/is_paying) - promoting to subscribed and skipping RC downgrade');
            if (isMountedRef.current) {
              setIsSubscribed(true);
              setSubscriptionState((dbUser?.subscription_state as SubscriptionState) || 'ACTIVE');
            }
            if (authState.user?.isSubscribed !== true) {
              updateUserSubscription(true);
            }
            return;
          }

          if (isMountedRef.current) {
            setIsSubscribed(false);
          }
          if (authState.user?.isSubscribed !== false) {
            updateUserSubscription(false);
            updateSubscriptionInDatabase(false);
          }
        } catch (err) {
          console.warn('🛒 📱 [REVENUECAT] DB guard check failed, keeping previous state:', err);
        }
      })();
      return;
    }

    if (isMountedRef.current) {
      setIsSubscribed(isActive);
    }

    if (isActive !== authState.user?.isSubscribed) {
      updateUserSubscription(isActive);
      updateSubscriptionInDatabase(isActive);
    }

  }, [authState.user?.isSubscribed, authState.user?.paymentMethod, updateUserSubscription, updateSubscriptionInDatabase]);

  const loadFallbackPrices = useCallback(async (userCountryCode: string | null): Promise<Product[]> => {
    const productId = 'com.virgoplay.hekaya.sub.monthly';
    const code = userCountryCode?.toLowerCase() || 'default';

    try {
      console.log('🛒 [loadFallbackPrices] Querying subscription_prices for country:', code);

      const { data, error } = await withSupabaseResult<any>(
        supabase
          .from('subscription_prices')
          .select('price, price_string, currency_code, period')
          .eq('product_id', productId)
          .eq('country_code', code)
          .maybeSingle(),
        { operation: 'subscription.fallbackPrice', logger: subLog }
      );

      let fallbackRow = data;

      if (!fallbackRow && code !== 'default') {
        console.log('🛒 [loadFallbackPrices] No price for country, trying default...');
        const { data: defaultData } = await withSupabaseResult<any>(
          supabase
            .from('subscription_prices')
            .select('price, price_string, currency_code, period')
            .eq('product_id', productId)
            .eq('country_code', 'default')
            .maybeSingle(),
          { operation: 'subscription.fallbackPriceDefault', logger: subLog }
        );
        fallbackRow = defaultData;
      }

      if (!fallbackRow) {
        console.warn('🛒 [loadFallbackPrices] No fallback price found in database');
        return [];
      }

      console.log('🛒 [loadFallbackPrices] Using fallback price:', fallbackRow.price_string);

      return [{
        productId: '$rc_monthly',
        price: fallbackRow.price?.toString() || '0',
        priceNumeric: parseFloat(fallbackRow.price) || 0,
        title: 'Subscription',
        description: '',
        localizedPrice: fallbackRow.price_string || '',
        currencyCode: fallbackRow.currency_code || 'EUR',
        isFallbackPrice: true,
      }];
    } catch (err) {
      console.error('🛒 [loadFallbackPrices] Error fetching fallback prices:', err);
      return [];
    }
  }, []);

  const loadProducts = useCallback(async (fallbackCountryCode?: string | null) => {
    if (productsLoadedRef.current) {
      console.log('🛒 [loadProducts] Products already loaded, skipping');
      return;
    }

    console.log('🛒 [loadProducts] Starting to load products...');

    let sdkSuccess = false;

    try {
      const offeringId = Platform.OS === 'ios'
        ? REVENUECAT_CONFIG.ios.offeringId
        : Platform.OS === 'web'
          ? REVENUECAT_CONFIG.web.offeringId
          : REVENUECAT_CONFIG.android.offeringId;

      console.log('🛒 [loadProducts] Platform:', Platform.OS, '| Offering ID:', offeringId);

      if (!offeringId) {
        console.warn(`🛒 [loadProducts] No offering ID configured for ${Platform.OS}`);
        return;
      }

      let offerings: any;
      if (Platform.OS === 'web') {
        console.log('🛒 [loadProducts] Fetching offerings from RevenueCat Web...');
        offerings = await revenueCatWebService.getOfferings();
      } else {
        console.log('🛒 [loadProducts] Fetching offerings from RevenueCat...');
        offerings = await revenueCatService.getOfferings();
      }

      console.log('🛒 [loadProducts] Offerings received:', {
        hasOfferings: !!offerings,
        hasCurrent: !!offerings?.current,
        currentIdentifier: offerings?.current?.identifier || null,
        allOfferingIds: offerings?.all ? Object.keys(offerings.all) : [],
      });

      if (offerings?.all) {
        for (const [id, off] of Object.entries<any>(offerings.all)) {
          const pkgs = Array.isArray(off?.availablePackages)
            ? off.availablePackages
            : Object.values(off?.availablePackages || {});
          console.log('🛒 [loadProducts] Dashboard offering:', {
            id,
            packagesCount: pkgs.length,
            packageIds: pkgs.map((p: any) => p?.identifier),
            productIds: pkgs.map((p: any) => p?.product?.identifier || p?.product?.productId),
          });
        }
      }

      const premiumFromAll = offerings?.all?.[offeringId];
      const currentOffering = premiumFromAll
        || (offerings?.current?.identifier === offeringId ? offerings.current : null)
        || offerings?.current;

      if (!currentOffering) {
        console.warn(`🛒 [loadProducts] Premium offering "${offeringId}" not found in offerings.all`);
        console.warn('🛒 [loadProducts] Available offering IDs:', offerings?.all ? Object.keys(offerings.all) : 'none');
        console.warn('🛒 [loadProducts] Check RevenueCat dashboard: offering exists, is attached to a product, and the product is available in the current store/sandbox.');
        console.warn('🛒 [loadProducts] Will attempt database fallback prices...');
      } else {
        const pkgsForDiag = Array.isArray(currentOffering.availablePackages)
          ? currentOffering.availablePackages
          : Object.values(currentOffering.availablePackages || {});
        if (pkgsForDiag.length === 0) {
          console.warn(`🛒 [loadProducts] Premium offering "${offeringId}" has 0 packages. Likely causes:`);
          console.warn('  - The attached product (expected iOS SKU: com.virgoplay.hekaya.sub.monthly) is not approved / not available in the current store.');
          console.warn('  - Running on simulator (IAP products only load on real devices with a Sandbox Tester account).');
          console.warn('  - StoreKit configuration mismatch for the app bundle id.');
        }

        console.log('🛒 [loadProducts] Using premium offering:', currentOffering.identifier);

        console.log('🛒 [loadProducts] Current offering:', {
          identifier: currentOffering.identifier,
          serverDescription: currentOffering.serverDescription,
          packagesCount: currentOffering.availablePackages?.length || 0,
        });

        const availablePackages = Array.isArray(currentOffering.availablePackages)
          ? currentOffering.availablePackages
          : Object.values(currentOffering.availablePackages || {});

        console.log('🛒 [loadProducts] Available packages:', availablePackages.length);

        if (availablePackages.length > 0) {
          const mappedProducts: Product[] = availablePackages.map((pkg: any) => {
            const rawPrice = pkg.product?.price;
            const priceNumeric = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice) || 0;
            console.log('🛒 [loadProducts] Mapping package:', {
              identifier: pkg.identifier,
              productId: pkg.product?.productId || pkg.product?.identifier,
              price: rawPrice,
              priceString: pkg.product?.priceString || pkg.product?.localizedPrice,
              currencyCode: pkg.product?.currencyCode,
            });
            return {
              productId: pkg.identifier,
              price: rawPrice?.toString() || '0',
              priceNumeric,
              title: pkg.product?.title || 'Subscription',
              description: pkg.product?.description || '',
              localizedPrice: pkg.product?.priceString || pkg.product?.localizedPrice || '',
              currencyCode: pkg.product?.currencyCode || '',
              isFallbackPrice: false,
            };
          });

          console.log('🛒 [loadProducts] Mapped products:', mappedProducts.length);

          if (isMountedRef.current) {
            setProducts(mappedProducts);
            productsLoadedRef.current = true;
            sdkSuccess = true;
            console.log('🛒 [loadProducts] Products set successfully:', mappedProducts.length, 'products');
          }
          return;
        }
      }
    } catch (error) {
      console.error('🛒 [loadProducts] Error loading products from SDK:', error);
    }

    if (!sdkSuccess && isMountedRef.current) {
      console.log('🛒 [loadProducts] SDK failed or returned no products, trying database fallback...');
      console.log('🛒 [loadProducts] This is expected when:');
      console.log('  - Products are not yet approved in App Store Connect / Google Play');
      console.log('  - Running on simulator without StoreKit Configuration file');
      console.log('  - Offering in RevenueCat references a product that does not exist in the store');
      const fallbackProducts = await loadFallbackPrices(fallbackCountryCode || null);
      if (fallbackProducts.length > 0 && isMountedRef.current) {
        setProducts(fallbackProducts);
        productsLoadedRef.current = true;
        console.log('🛒 [loadProducts] Fallback products set from database:', fallbackProducts.length);
      } else {
        console.warn('🛒 [loadProducts] No fallback prices available in database either. Paywall will have no prices to display.');
      }
    }
  }, [loadFallbackPrices]);

  const initializeRevenueCatWeb = useCallback(async () => {
    if (initializationInProgressRef.current) {
      return;
    }

    const smartuserId = authState.user?.smartuserId;

    if (!smartuserId) {
      if (isMountedRef.current) {
        setIsIAPReady(false);
      }
      return;
    }

    try {
      initializationInProgressRef.current = true;

      const shouldInitialize = !revenueCatInitializedRef.current;
      const shouldIdentifyUser = lastLoggedInUserRef.current !== smartuserId;

      if (!shouldInitialize && !shouldIdentifyUser) {
        initializationInProgressRef.current = false;
        return;
      }

      if (shouldInitialize || shouldIdentifyUser) {
        console.log('🛒 [Context Web] Configuring RevenueCat Web for user:', smartuserId);
        const configured = await revenueCatWebService.configure(smartuserId);

        if (!configured) {
          console.warn('🛒 [Context Web] RevenueCat Web service not ready after configuration (missing API key?)');
          if (isMountedRef.current) {
            setWebConfigError(true);
          }
          initializationInProgressRef.current = false;
          return;
        }

        revenueCatInitializedRef.current = true;
        lastLoggedInUserRef.current = smartuserId;

        await loadProducts(countryCodeRef.current);

        const customerInfo = await revenueCatWebService.getCustomerInfo();
        if (customerInfo) {
          updateSubscriptionStatusFromCustomerInfo(customerInfo, smartuserId);
        }

        if (isMountedRef.current) {
          setIsIAPReady(true);
        }

        console.log('🛒 [Context Web] RevenueCat Web initialized successfully');
      }
    } catch (error) {
      console.error('🛒 [Context Web] RevenueCat Web initialization error:', error);
      if (isMountedRef.current) {
        setIsIAPReady(false);
      }
    } finally {
      initializationInProgressRef.current = false;
    }
  }, [authState.user?.smartuserId, loadProducts, updateSubscriptionStatusFromCustomerInfo]);

  const initializeRevenueCatNative = useCallback(async () => {
    if (initializationInProgressRef.current) {
      return;
    }

    const smartuserId = authState.user?.smartuserId;

    if (!smartuserId) {
      if (isMountedRef.current) {
        setIsIAPReady(false);
      }
      return;
    }

    try {
        initializationInProgressRef.current = true;

        if (listenerCleanupRef.current) {
          listenerCleanupRef.current();
          listenerCleanupRef.current = null;
        }

        const shouldInitialize = !revenueCatInitializedRef.current;
        const shouldIdentifyUser = lastLoggedInUserRef.current !== smartuserId;

        if (!shouldInitialize && !shouldIdentifyUser) {
          initializationInProgressRef.current = false;
          return;
        }

        if (shouldInitialize) {
          await revenueCatService.initialize(smartuserId);

          if (!revenueCatService.isReady()) {
            console.warn('🛒 [Context] RevenueCat service not ready after initialization');
            initializationInProgressRef.current = false;
            return;
          }

          revenueCatInitializedRef.current = true;

          await loadProducts(countryCodeRef.current);

          if (isMountedRef.current) {
            setIsIAPReady(true);
          }
        }

        if (shouldIdentifyUser) {
          lastLoggedInUserRef.current = null;
          revenueCatService.clearUserState();

          await revenueCatService.logIn(smartuserId);
          lastLoggedInUserRef.current = smartuserId;
          const freshCustomerInfo = await revenueCatService.getCustomerInfo(true);

          updateSubscriptionStatusFromCustomerInfo(freshCustomerInfo, smartuserId);

          if (!listenerCleanupRef.current) {
            const removeListener = revenueCatService.addCustomerInfoListener((customerInfo) => {
              if (isMountedRef.current) {
                updateSubscriptionStatusFromCustomerInfo(customerInfo, smartuserId);
              }
            }, smartuserId);
            listenerCleanupRef.current = removeListener;
          }
        }

      } catch (error) {
        console.error('🛒 [Context] RevenueCat initialization error:', error);
        if (isMountedRef.current) {
          setIsIAPReady(false);
        }
    } finally {
      initializationInProgressRef.current = false;
    }
  }, [authState.user?.smartuserId, loadProducts, updateSubscriptionStatusFromCustomerInfo]);

  const initializeRevenueCat = useCallback(async () => {
    if (Platform.OS === 'web') {
      return initializeRevenueCatWeb();
    }
    return initializeRevenueCatNative();
  }, [initializeRevenueCatWeb, initializeRevenueCatNative]);

  useEffect(() => {
    const effectTimestamp = new Date().toISOString();

    // CRITICAL: RevenueCat is ONLY initialized when user is authenticated
    // This prevents anonymous ID creation and ensures all subscriptions are tied to real users
    if (!authState.user?.smartuserId) {
      if (isMountedRef.current) {
        setIsIAPReady(false);
        setIsSubscribed(false);
        setProducts([]);
        setSubscriptionState(null);
        setGracePeriodEndDate(null);
      }

      revenueCatInitializedRef.current = false;
      lastLoggedInUserRef.current = null;
      productsLoadedRef.current = false;

      return;
    }

    console.log('🛒 [INIT] Entry state:', {
      smartuserId: authState.user?.smartuserId,
      paymentMethod: authState.user?.paymentMethod,
      platform: Platform.OS,
      isSubscribed: authState.user?.isSubscribed,
    });

    if (Platform.OS === 'web') {
      const currentSmartuserId = authState.user.smartuserId;
      const paymentMethod = authState.user?.paymentMethod;
      let cancelled = false;

      supabase
        .from('users')
        .select('is_paying, subscription_state, grace_period_end_date, payment_method')
        .eq('smartuser_id', currentSmartuserId)
        .maybeSingle()
        .then(({ data }) => {
          if (cancelled) return;
          const isActive = data?.is_paying === true;
          const dbPaymentMethod = data?.payment_method || paymentMethod;
          const isAuthoritative = dbPaymentMethod === 'dcb' || data?.subscription_state === 'ACTIVE' || isActive;

          if (isAuthoritative) {
            console.log('🛒 [INIT] Web + authoritative DB subscription detected - using database, skipping RC web init');
            if (isMountedRef.current) {
              setIsSubscribed(isActive);
              setSubscriptionState((data?.subscription_state as SubscriptionState) || null);
              setGracePeriodEndDate(data?.grace_period_end_date || null);
              setIsLoading(false);
            }
            if (authState.user?.isSubscribed !== isActive) {
              updateUserSubscription(isActive);
            }
            return;
          }

          initializeRevenueCat().catch(error => {
            console.error('🚨 [Context] Unhandled error in RevenueCat initialization:', error);
            if (isMountedRef.current) setIsIAPReady(false);
            initializationInProgressRef.current = false;
          });
        })
        .catch(() => {
          if (cancelled) return;
          initializeRevenueCat().catch(error => {
            console.error('🚨 [Context] Unhandled error in RevenueCat initialization:', error);
            if (isMountedRef.current) setIsIAPReady(false);
            initializationInProgressRef.current = false;
          });
        });

      return () => {
        cancelled = true;
        if (listenerCleanupRef.current) {
          listenerCleanupRef.current();
          listenerCleanupRef.current = null;
        }
      };
    }

    const isNewLogin = !lastLoggedInUserRef.current && authState.user?.smartuserId;
    const initDelay = isNewLogin ? 200 : 0;

    console.log('🛒 🕐 [INIT DELAY] ===== REVENUECAT INITIALIZATION TIMING =====');
    console.log('🛒 🕐 [INIT DELAY] Timestamp:', effectTimestamp);
    console.log('🛒 🕐 [INIT DELAY] Is new login:', isNewLogin);
    console.log('🛒 🕐 [INIT DELAY] Last logged in user:', lastLoggedInUserRef.current);
    console.log('🛒 🕐 [INIT DELAY] Current user:', authState.user?.smartuserId);
    console.log('🛒 🕐 [INIT DELAY] Applied delay:', initDelay, 'ms');

    const initStartTime = Date.now();

    // Apply delay if this is a new login
    const timeoutId = setTimeout(() => {
      console.log('🛒 🕐 [INIT DELAY] ⏱️ Delay completed, starting initialization...');

      initializeRevenueCat()
        .then(() => {
          const initEndTime = Date.now();
          const duration = initEndTime - initStartTime;
          console.log('🛒 🕐 [INIT DELAY] ✅ Initialization successful');
          console.log('🛒 🕐 [INIT DELAY] Total duration (including delay):', duration, 'ms');
          console.log('🛒 🕐 [INIT DELAY] Actual init duration:', duration - initDelay, 'ms');
        })
        .catch(error => {
          const initEndTime = Date.now();
          const duration = initEndTime - initStartTime;
          console.error('🚨 [Context] Unhandled error in RevenueCat initialization:', error);
          console.error('🛒 ⏰ [TIMING] ❌ RevenueCat initialization FAILED');
          console.error('🛒 ⏰ [TIMING] Duration before failure:', duration, 'ms');
          if (isMountedRef.current) {
            setIsIAPReady(false);
          }
          initializationInProgressRef.current = false;
        });
    }, initDelay);

    return () => {
      clearTimeout(timeoutId);
      if (listenerCleanupRef.current) {
        listenerCleanupRef.current();
        listenerCleanupRef.current = null;
      }
    };
  }, [authState.user?.smartuserId, authState.user?.paymentMethod, initializeRevenueCat, updateUserSubscription]);

  const purchaseSubscriptionWeb = useCallback(async (productId?: string) => {
    if (!authState.user?.smartuserId) {
      console.error('🛒 [Context Web] Purchase blocked: User not authenticated');
      Alert.alert(t('error'), t('pleaseSignIn'));
      return;
    }

    if (isPurchasing || !isIAPReady) {
      return;
    }

    const product = products.find(p => p.productId === productId) || products[0];
    const packageId = productId || product?.productId;

    if (!packageId) {
      console.error('🛒 [Context Web] Purchase blocked: No product available');
      return;
    }

    const productInfo: PaywallProductInfo = {
      productId: packageId,
      productTitle: product?.title,
      localizedPrice: product?.localizedPrice,
    };

    try {
      setIsPurchasing(true);
      trackPurchaseInitiated(productInfo);

      const result = await revenueCatWebService.purchase(packageId, authState.user?.email || undefined);

      if (result.userCancelled) {
        trackPurchaseCancel(productInfo);
        return;
      }

      if (!result.success) {
        trackPurchaseFailed(productInfo, result.error || 'Unknown error');
        Alert.alert(t('error'), result.error || t('webPaymentUnavailable'));
        return;
      }

      if (result.customerInfo) {
        updateSubscriptionStatusFromCustomerInfo(result.customerInfo, authState.user.smartuserId);
      }

      trackPurchaseSuccess(productInfo);
    } catch (error: any) {
      console.error('🛒 [Context Web] Purchase error:', error);
      trackPurchaseFailed(productInfo, error?.message || 'Purchase error');
    } finally {
      setIsPurchasing(false);
    }
  }, [products, authState.user, t, isPurchasing, isIAPReady]);

  const purchaseSubscriptionNative = useCallback(async (productId?: string) => {
    console.log('🛒 [Context] purchaseSubscriptionNative: entry', {
      productId,
      isPurchasing,
      isIAPReady,
      serviceReady: revenueCatService.isReady(),
      smartuserId: authState.user?.smartuserId,
    });

    if (!authState.user?.smartuserId) {
      console.error('🛒 [Context] Purchase blocked: User not authenticated');
      Alert.alert(t('error'), t('pleaseSignIn'));
      return;
    }

    if (isPurchasing) {
      console.warn('🛒 [Context] Purchase blocked: purchase already in progress');
      return;
    }

    if (!revenueCatService.isReady() || !isIAPReady) {
      console.warn('🛒 [Context] RevenueCat not ready, attempting on-demand initialization...');
      try {
        const forceInit = forceInitializeRevenueCatRef.current;
        if (!forceInit) throw new Error('Force init not available');
        await forceInit(authState.user.smartuserId);
      } catch (initError) {
        console.error('🛒 [Context] On-demand init failed:', initError);
        Alert.alert(t('error'), t('paymentInitializing'));
        return;
      }

      if (!revenueCatService.isReady()) {
        console.error('🛒 [Context] Purchase blocked: RevenueCat still not ready after retry');
        Alert.alert(t('error'), t('paymentInitializing'));
        return;
      }
    }

    let productInfo: PaywallProductInfo | null = null;

    try {
      setIsPurchasing(true);

      const offerings = await revenueCatService.getOfferings();
      const premiumOfferingId = Platform.OS === 'ios'
        ? REVENUECAT_CONFIG.ios.offeringId
        : REVENUECAT_CONFIG.android.offeringId;

      console.log('🛒 [Purchase] Platform:', Platform.OS, '| Looking for offering:', premiumOfferingId);
      console.log('🛒 [Purchase] Offerings received:', {
        hasCurrent: !!offerings?.current,
        currentId: offerings?.current?.identifier,
        allIds: offerings?.all ? Object.keys(offerings.all) : [],
      });

      const currentOffering = offerings?.all?.[premiumOfferingId]
        || (offerings?.current?.identifier === premiumOfferingId ? offerings.current : null)
        || offerings?.current
        || null;

      if (!currentOffering) {
        console.error('🛒 [Purchase] No offering found. Available:', offerings?.all ? Object.keys(offerings.all) : 'none');
        throw new Error('No subscription packages available');
      }

      console.log('🛒 [Purchase] Using offering:', currentOffering.identifier);

      const availablePackages = Array.isArray(currentOffering.availablePackages)
        ? currentOffering.availablePackages
        : Object.values(currentOffering.availablePackages || {});

      console.log('🛒 [Purchase] Available packages:', availablePackages.length, availablePackages.map((p: any) => p?.identifier));

      if (availablePackages.length === 0) {
        console.error('🛒 [Purchase] Offering has 0 packages. Likely causes: product not approved in store, running on simulator without sandbox tester, or StoreKit config mismatch.');
        throw new Error('No subscription packages available');
      }

      let packageToPurchase: any;
      if (productId) {
        packageToPurchase = availablePackages.find((pkg: any) => pkg.identifier === productId);
        if (!packageToPurchase) {
          console.warn('🛒 Package not found for productId:', productId, '- falling back to first package');
          packageToPurchase = availablePackages[0];
        }
      } else {
        packageToPurchase = availablePackages[0];
      }

      productInfo = {
        productId: packageToPurchase?.identifier || productId || 'unknown',
        productTitle: packageToPurchase?.product?.title,
        localizedPrice: packageToPurchase?.product?.priceString,
        currencyCode: packageToPurchase?.product?.currencyCode,
      };

      trackPurchaseInitiated(productInfo);

      const { customerInfo, productIdentifier } = await revenueCatService.purchasePackage(packageToPurchase);

      updateSubscriptionStatusFromCustomerInfo(customerInfo);

      trackPurchaseSuccess(productInfo, {
        transactionId: customerInfo?.latestExpirationDate,
        originalTransactionId: customerInfo?.originalAppUserId,
        purchaseDate: customerInfo?.originalPurchaseDate,
      });

      if (Platform.OS === 'ios' && authState.user?.smartuserId) {
        appleReceiptService.fetchAndValidateReceipt(
          authState.user.smartuserId,
          {
            forceRefresh: true,
            sendToBackend: true,
            storeInDatabase: true,
            metadata: {
              transactionId: customerInfo.originalPurchaseDate,
              productId: productIdentifier,
              revenueCatUserId: customerInfo.originalAppUserId,
            },
          }
        ).then(result => {
          if (result.success) {
            console.log('🧾 [Context] ✅ Apple receipt validated successfully');
          } else {
            console.warn('🧾 [Context] ⚠️ Apple receipt validation failed (non-blocking)');
          }
        }).catch(error => {
          console.error('🧾 [Context] ❌ Error validating Apple receipt (non-blocking):', error);
        });
      }

      console.log('🛒 🔄 [PURCHASE] Starting defensive refresh from database after successful purchase...');
      console.log('🛒 🔄 [PURCHASE] Waiting 2s for webhook to process the transaction...');

      setTimeout(() => {
        forceRefreshSubscriptionFromDatabase().then(confirmed => {
          if (confirmed) {
            console.log('🛒 🔄 [PURCHASE] ✅ Subscription status confirmed from database');
          } else {
            console.warn('🛒 🔄 [PURCHASE] ⚠️ Subscription not yet confirmed in database after retries');
            console.warn('🛒 🔄 [PURCHASE] The webhook may still be processing - status will update on next app launch');
          }
        }).catch(error => {
          console.error('🛒 🔄 [PURCHASE] ❌ Error during defensive refresh:', error);
        });
      }, 2000);

      Alert.alert(t('success'), t('subscriptionActivated'));
    } catch (error: any) {
      if (error.userCancelled) {
        console.log('🛒 [Context] Purchase cancelled by user - propagating to caller');
        if (productInfo) {
          trackPurchaseCancel(productInfo);
        }
        throw error;
      }

      console.error('🛒 [Context] Purchase error:', error);
      const errorMsg = error?.message || '';

      if (productInfo) {
        trackPurchaseFailed(productInfo, {
          errorMessage: errorMsg || 'Purchase failed',
          errorCode: error?.code || 'UNKNOWN',
        });
      }

      if (errorMsg.includes('BillingWrapper') || errorMsg.includes('not attached')) {
        console.error('🛒 [Context] Android billing client error detected during purchase');
        const billingStatus = revenueCatService.getBillingClientStatus();
        console.error('🛒 [Context] Billing client status:', billingStatus);
        console.error('🛒 [Context] Diagnostics:\n' + billingStatus.diagnostics);
        Alert.alert(
          t('error'),
          'Payment system initializing. Please wait a moment and try again.'
        );
      } else {
        Alert.alert(t('error'), errorMsg || 'Purchase failed');
      }
    } finally {
      if (isMountedRef.current) {
        setIsPurchasing(false);
      }
    }
  }, [isPurchasing, isIAPReady, authState.user, t, updateSubscriptionStatusFromCustomerInfo, forceRefreshSubscriptionFromDatabase]);

  const purchaseSubscription = useCallback(async (productId?: string) => {
    if (Platform.OS === 'web') {
      return purchaseSubscriptionWeb(productId);
    }
    return purchaseSubscriptionNative(productId);
  }, [purchaseSubscriptionWeb, purchaseSubscriptionNative]);

  const restorePurchases = useCallback(async () => {
    if (Platform.OS === 'web') {
      console.log('🛒 [Context Web] Checking subscription status...');
      try {
        const customerInfo = await revenueCatWebService.getCustomerInfo();
        const premiumEntitlementId = REVENUECAT_CONFIG.web.entitlementId;
        if (customerInfo && customerInfo.entitlements?.active?.[premiumEntitlementId]) {
          updateSubscriptionStatusFromCustomerInfo(customerInfo);
          Alert.alert(t('success'), t('purchasesRestored'));
        } else {
          Alert.alert(t('info'), t('noPurchasesToRestore'));
        }
      } catch (error) {
        console.error('🛒 [Context Web] Error checking subscription:', error);
        Alert.alert(t('error'), 'Failed to check subscription status');
      }
      return;
    }

    if (!authState.user?.smartuserId) {
      console.error('🛒 [Context] Restore blocked: User not authenticated');
      Alert.alert(t('error'), t('pleaseSignIn'));
      return;
    }

    if (!revenueCatService.isReady()) {
      console.error('🛒 [Context] Restore blocked: RevenueCat not initialized');
      Alert.alert(t('error'), 'In-app purchases not available. Please try again.');
      return;
    }

    try {
      const { customerInfo } = await revenueCatService.restorePurchases();

      // Verify customerInfo is valid
      if (!customerInfo) {
        console.error('🛒 [Context] Restore failed: customerInfo is undefined');
        Alert.alert(t('error'), 'Failed to retrieve subscription information');
        return;
      }

      console.log('🛒 [Context] Customer info retrieved:', {
        hasEntitlements: !!customerInfo.entitlements,
        activeCount: Object.keys(customerInfo.entitlements?.active || {}).length,
        originalAppUserId: customerInfo.originalAppUserId
      });

      const entitlementId = Platform.OS === 'ios'
        ? REVENUECAT_CONFIG.ios.entitlementId
        : REVENUECAT_CONFIG.android.entitlementId;

      if (!entitlementId) {
        console.warn(`🛒 No entitlement ID configured for ${Platform.OS}`);
        Alert.alert(t('error'), 'Configuration error');
        return;
      }

      const hasActiveSubscription = customerInfo.entitlements?.active?.[entitlementId] != null;

      if (hasActiveSubscription) {
        console.log('🛒 Subscription restored successfully');
        updateSubscriptionStatusFromCustomerInfo(customerInfo);

        // Fetch and validate Apple receipt after restore (non-blocking for iOS only)
        if (Platform.OS === 'ios' && authState.user?.smartuserId) {
          console.log('🧾 [Context] Fetching Apple receipt after restore...');
          appleReceiptService.fetchAndValidateReceipt(
            authState.user.smartuserId,
            {
              forceRefresh: true,
              sendToBackend: true,
              storeInDatabase: true,
              metadata: {
                source: 'restore_purchases',
                revenueCatUserId: customerInfo.originalAppUserId,
              },
            }
          ).then(result => {
            if (result.success) {
              console.log('🧾 [Context] ✅ Apple receipt validated after restore');
            } else {
              console.warn('🧾 [Context] ⚠️ Apple receipt validation failed after restore (non-blocking)');
            }
          }).catch(error => {
            console.error('🧾 [Context] ❌ Error validating Apple receipt after restore (non-blocking):', error);
          });
        }

        Alert.alert(t('success'), t('purchasesRestored'));
      } else {
        console.log('🛒 No active subscription found');
        Alert.alert(t('info'), t('noPurchasesToRestore'));
      }
    } catch (error: any) {
      console.error('🛒 Restore error:', error);

      const errorMsg = error?.message || '';

      // Provide specific error message for billing wrapper issues
      if (errorMsg.includes('BillingWrapper') || errorMsg.includes('not attached')) {
        console.error('🛒 [Context] Android billing client error detected during restore');
        const billingStatus = revenueCatService.getBillingClientStatus();
        console.error('🛒 [Context] Billing client status:', billingStatus);
        console.error('🛒 [Context] Diagnostics:\n' + billingStatus.diagnostics);
        Alert.alert(
          t('error'),
          'Payment system initializing. Please wait a moment and try again.'
        );
      } else {
        Alert.alert(t('error'), 'Failed to restore purchases');
      }
    }
  }, [t, updateSubscriptionStatusFromCustomerInfo]);

  const checkSubscriptionStatus = useCallback(async () => {
    const smartuserId = authState.user?.smartuserId;

    if (!smartuserId) {
      if (isMountedRef.current) {
        setIsSubscribed(false);
        setIsLoading(false);
      }
      return;
    }

    try {
      if (isMountedRef.current) {
        setIsLoading(true);
      }

      if (authState.user?.paymentMethod === 'dcb') {
        console.log('🛒 [Subscription] DCB user detected - skipping RevenueCat, querying database directly');
      } else if (Platform.OS !== 'web' && revenueCatService.isReady() && revenueCatService.isUserIdentified()) {
        const customerInfo = await revenueCatService.getCustomerInfo();
        const originalAppUserId = customerInfo?.originalAppUserId;

        if (originalAppUserId?.startsWith('$RCAnonymousID:')) {
          console.log('🛒 [Subscription] RevenueCat returned anonymous data, falling back to database');
        } else {
          updateSubscriptionStatusFromCustomerInfo(customerInfo);
          return;
        }
      }

      if (Platform.OS === 'web') {
        console.log('🛒 [Subscription] Web platform: always querying database for fresh is_paying value...');
        const { data, error } = await withSupabaseResult<{ is_paying: boolean; subscription_state: string | null; grace_period_end_date: string | null } | null>(
          supabase
            .from('users')
            .select('is_paying, subscription_state, grace_period_end_date')
            .eq('smartuser_id', smartuserId)
            .maybeSingle(),
          { operation: 'subscription.check.web', logger: subLog }
        );

        if (error) {
          console.error('Error checking subscription:', error.message);
          return;
        }

        const isActive = data?.is_paying === true;
        console.log('🛒 [Subscription] Database is_paying value:', isActive);

        if (isMountedRef.current) {
          setIsSubscribed(isActive);
          setSubscriptionState(data?.subscription_state || null);
          setGracePeriodEndDate(data?.grace_period_end_date || null);
        }

        if (authState.user?.isSubscribed !== isActive) {
          updateUserSubscription(isActive);
        }

        userPreferencesService.updateIsPaying(smartuserId, isActive).catch(err => {
          console.warn('🛒 [Subscription] Failed to update cache:', err);
        });
      } else {
        const cachedIsPaying = userPreferencesService.getIsPaying(smartuserId);

        if (cachedIsPaying === true) {
          console.log('🛒 [Subscription] Using cached is_paying=true (optimistic)');
          if (isMountedRef.current) {
            setIsSubscribed(true);
          }
          if (authState.user?.isSubscribed !== true) {
            updateUserSubscription(true);
          }
        }

        console.log('🛒 [Subscription] Querying database for authoritative is_paying value...');
        const { data, error } = await withSupabaseResult<{ is_paying: boolean; subscription_state: string | null; grace_period_end_date: string | null } | null>(
          supabase
            .from('users')
            .select('is_paying, subscription_state, grace_period_end_date')
            .eq('smartuser_id', smartuserId)
            .maybeSingle(),
          { operation: 'subscription.check.native', logger: subLog }
        );

        if (error) {
          console.error('Error checking subscription:', error.message);
          return;
        }

        const isActive = data?.is_paying === true;
        console.log('🛒 [Subscription] DB authoritative is_paying:', isActive, '| cached was:', cachedIsPaying);

        if (isMountedRef.current) {
          setIsSubscribed(isActive);
          setSubscriptionState(data?.subscription_state || null);
          setGracePeriodEndDate(data?.grace_period_end_date || null);
        }

        if (authState.user?.isSubscribed !== isActive) {
          updateUserSubscription(isActive);
        }

        if (cachedIsPaying !== isActive) {
          userPreferencesService.updateIsPaying(smartuserId, isActive).catch(err => {
            console.warn('🛒 [Subscription] Failed to sync cache:', err);
          });
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [authState.user?.smartuserId, authState.user?.isSubscribed, authState.user?.paymentMethod, updateUserSubscription, updateSubscriptionStatusFromCustomerInfo]);

  useEffect(() => {
    if (!campaignCountriesLanguagesId) {
      if (isMountedRef.current) {
        setEpisodePriceCoins(0);
      }
      return;
    }

    const price = campaignCache.getEpisodePrice(campaignCountriesLanguagesId);
    if (isMountedRef.current) {
      setEpisodePriceCoins(price);
      console.log('🪙 [Context] Episode price from cache:', price);
    }
  }, [campaignCountriesLanguagesId]);

  const deductCoinsForEpisode = useCallback(async (price: number, episodeId: string): Promise<boolean> => {
    console.log('🪙 [Context] deductCoinsForEpisode called:', { price, episodeId });

    let smartuserId = authStateUserRef.current?.smartuserId;

    if (!smartuserId) {
      console.log('🪙 [Context] ⚠️ authState.user not available yet - waiting for auth to propagate via ref...');

      const AUTH_MAX_WAIT_MS = 2000;
      const AUTH_POLL_INTERVAL_MS = 50;
      let authWaited = 0;

      while (!smartuserId && authWaited < AUTH_MAX_WAIT_MS) {
        await new Promise(resolve => setTimeout(resolve, AUTH_POLL_INTERVAL_MS));
        authWaited += AUTH_POLL_INTERVAL_MS;
        smartuserId = authStateUserRef.current?.smartuserId;

        if (smartuserId) {
          console.log('🪙 [Context] ✅ authState.user became available after', authWaited, 'ms');
          break;
        }
      }

      if (!smartuserId) {
        console.log('🪙 [Context] No user logged in after', AUTH_MAX_WAIT_MS, 'ms wait - cannot deduct coins');
        return false;
      }
    }

    if (smartuserId && episodeId && isEpisodePurchased(smartuserId, episodeId)) {
      console.log('🪙 [Context] ✅ Episode already purchased in local storage - no deduction needed');
      console.log('🪙 [Context] Episode ID:', episodeId);
      return true;
    }

    let effectiveGamification = userGamificationRef.current;

    if (!effectiveGamification) {
      console.log('🪙 [Context] ⚠️ userGamification not loaded yet - waiting via ref...');

      const MAX_WAIT_MS = 3000;
      const POLL_INTERVAL_MS = 100;
      let waited = 0;

      while (!effectiveGamification && waited < MAX_WAIT_MS) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        waited += POLL_INTERVAL_MS;

        if (userGamificationRef.current) {
          effectiveGamification = userGamificationRef.current;
          console.log('🪙 [Context] ✅ userGamification became available after', waited, 'ms');
          break;
        }
      }

      if (!effectiveGamification) {
        console.log('🪙 [Context] userGamification still null after wait, querying DB directly');

        try {
          const { data: gamificationData, error } = await withSupabaseResult<any>(
            supabase
              .from('user_gamification')
              .select('smartuser_id, total_coins, consecutive_days_streak, last_visit_date')
              .eq('smartuser_id', smartuserId)
              .maybeSingle(),
            { operation: 'subscription.gamificationFallback', logger: subLog }
          );

          if (error) {
            console.error('🪙 [Context] DB query error:', error.message);
            return false;
          }

          if (!gamificationData) {
            console.log('🪙 [Context] No gamification data found for user in DB');
            return false;
          }

          effectiveGamification = gamificationData;
          console.log('🪙 [Context] ✅ Loaded gamification from DB fallback:', {
            total_coins: effectiveGamification.total_coins
          });
        } catch (dbError) {
          console.error('🪙 [Context] Exception querying DB:', dbError);
          return false;
        }
      }
    }

    if (effectiveGamification.total_coins < price) {
      console.log('🪙 [Context] Insufficient coins:', {
        available: effectiveGamification.total_coins,
        required: price
      });
      return false;
    }

    try {
      await processEvent('deduct_coins', { cost: price, episodeId });
      console.log('🪙 [Context] Coin deduction successful');

      triggerCelebration();
      console.log('🎉 [Context] Celebration triggered!');

      return true;
    } catch (error) {
      console.error('🪙 [Context] Error deducting coins:', error);
      return false;
    }
  }, [processEvent, triggerCelebration]);

  const updateSubscriptionStatus = async (subscribed: boolean) => {
    const smartuserId = authState.user?.smartuserId;
    if (!smartuserId) return;

    try {
      const { error } = await withSupabaseResult(
        supabase
          .from('users')
          .update({ is_paying: subscribed })
          .eq('smartuser_id', smartuserId),
        { operation: 'subscription.updateStatus', logger: subLog }
      );

      if (error) {
        console.error('Error updating subscription:', error.message);
        return;
      }

      if (isMountedRef.current) {
        setIsSubscribed(subscribed);
      }

      if (authState.user?.isSubscribed !== subscribed) {
        updateUserSubscription(subscribed);
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
    }
  };

  const canAccessEpisode = useCallback((isFree: boolean | string | number | null | undefined, episodeId?: string): boolean => {
    const episodeIsFree = isEpisodeFree(isFree);

    if (episodeIsFree) return true;
    if (!authState.user && !dcbAnonymousAccess) return false;
    if (isSubscribed || dcbAnonymousAccess) return true;
    if (episodeId && authState.user?.smartuserId && isEpisodePurchased(authState.user.smartuserId, episodeId)) {
      console.log(`[canAccessEpisode] Episode ${episodeId} was purchased with coins - granting access`);
      return true;
    }
    return false;
  }, [authState.user, isSubscribed, dcbAnonymousAccess]);

  const forceInitializeRevenueCat = useCallback(async (smartuserId: string) => {
    if (Platform.OS === 'web') {
      return;
    }

    if (!smartuserId) {
      console.error('🛒 [Context] forceInitializeRevenueCat: No smartuserId provided');
      throw new Error('smartuserId is required for RevenueCat initialization');
    }

    try {
      // Wait if initialization is already in progress
      if (initializationInProgressRef.current) {
        let attempts = 0;
        while (initializationInProgressRef.current && attempts < 50) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        if (initializationInProgressRef.current) {
          console.warn('🛒 [Context] forceInitializeRevenueCat: Timeout waiting for initialization');
        }
      }

      // Check if service is ready (it might have been initialized by the regular effect)
      const serviceReady = revenueCatService.isReady();
      // Initialize SDK if not already initialized
      if (!serviceReady && !revenueCatInitializedRef.current) {
        console.log('🛒 [Context] forceInitializeRevenueCat: Initializing RevenueCat SDK...');
        await revenueCatService.initialize(smartuserId);
        revenueCatInitializedRef.current = true;

        // Load products before setting IAP ready
        console.log('🛒 [Context] forceInitializeRevenueCat: Loading products...');
        await loadProducts(countryCodeRef.current);

        if (isMountedRef.current) {
          setIsIAPReady(true);
          console.log('🛒 [Context] forceInitializeRevenueCat: IAP is now ready');
        }

      } else if (serviceReady) {
        console.log('🛒 [Context] forceInitializeRevenueCat: SDK already ready, checking products...');
        // Make sure products are loaded even if SDK was already ready
        if (!productsLoadedRef.current) {
          await loadProducts(countryCodeRef.current);
        }
        if (isMountedRef.current && !isIAPReady) {
          setIsIAPReady(true);
        }
      }

      // Verify SDK is ready now
      if (!revenueCatService.isReady()) {
        console.error('🛒 [Context] forceInitializeRevenueCat: SDK not ready after initialization attempt');
        throw new Error('RevenueCat SDK failed to initialize');
      }

      lastLoggedInUserRef.current = null;
      revenueCatService.clearUserState();

      const result = await revenueCatService.logIn(smartuserId);
      lastLoggedInUserRef.current = smartuserId;
      const freshCustomerInfo = await revenueCatService.getCustomerInfo(true);

      updateSubscriptionStatusFromCustomerInfo(freshCustomerInfo, smartuserId);

      if (!listenerCleanupRef.current) {
        const removeListener = revenueCatService.addCustomerInfoListener((customerInfo) => {
          if (isMountedRef.current) {
            console.log('🛒 🔔 [LISTENER] Customer info updated from listener');
            updateSubscriptionStatusFromCustomerInfo(customerInfo, smartuserId);
          }
        }, smartuserId);
        listenerCleanupRef.current = removeListener;
      }

    } catch (error) {
      console.error('🛒 [Context] forceInitializeRevenueCat: ❌ Error during forced initialization:', error);
      throw error;
    }
  }, [updateSubscriptionStatusFromCustomerInfo, isIAPReady, loadProducts]);

  useEffect(() => {
    revenueCatInitializer.setForceInitFunction(forceInitializeRevenueCat);
    forceInitializeRevenueCatRef.current = forceInitializeRevenueCat;
    return () => {
      revenueCatInitializer.clear();
    };
  }, [forceInitializeRevenueCat]);

  useEffect(() => {
    if (authState.user) {
      // Wrap in promise to properly handle async errors
      checkSubscriptionStatus().catch(error => {
        console.error('🚨 [Context] Unhandled error in checkSubscriptionStatus:', error);
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      });
    } else if (isMountedRef.current) {
      setIsSubscribed(false);
      setIsLoading(false);
    }
  }, [authState.user, checkSubscriptionStatus]);

  // On web, when no authenticated user, check if a valid DCB pending cookie exists
  useEffect(() => {
    if (Platform.OS !== 'web' || authState.user) {
      if (authState.user && dcbAnonymousAccess) {
        setDcbAnonymousAccess(false);
        setDcbAccessExpiresAt(null);
      }
      return;
    }

    let cancelled = false;

    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
    const supabaseAnonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !supabaseAnonKey) return;

    fetch(`${supabaseUrl}/functions/v1/manage-users`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({ action: 'dcb_check_access' }),
    })
      .then(res => res.json())
      .then((data: { hasAccess?: boolean; expiresAt?: string }) => {
        if (cancelled || !isMountedRef.current) return;
        if (data.hasAccess) {
          console.log('[DCBAnonymous] Valid DCB cookie found, granting anonymous access until', data.expiresAt);
          setDcbAnonymousAccess(true);
          setDcbAccessExpiresAt(data.expiresAt ?? null);
        } else {
          setDcbAnonymousAccess(false);
          setDcbAccessExpiresAt(null);
        }
      })
      .catch(() => {
        if (cancelled || !isMountedRef.current) return;
        setDcbAnonymousAccess(false);
        setDcbAccessExpiresAt(null);
      });

    return () => { cancelled = true; };
  }, [authState.user]);

  const value: SubscriptionContextValue = {
    isSubscribed,
    isLoading,
    isPurchasing,
    products,
    isIAPReady,
    webConfigError,
    subscriptionState,
    gracePeriodEndDate,
    dcbAnonymousAccess,
    dcbAccessExpiresAt,
    purchaseSubscription,
    restorePurchases,
    checkSubscriptionStatus,
    updateSubscriptionStatus,
    canAccessEpisode,
    episodePriceCoins,
    deductCoinsForEpisode,
    forceInitializeRevenueCat,
    forceRefreshSubscriptionFromDatabase,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
}
